import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Icons } from './Icons';
import { useAuth } from '../hooks/useAuth';
import NotificationBell from './NotificationBell';

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState({
    '데이터 관리': true,
    '설정': false
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const menuItems = [
    { path: '/', label: '대시보드', icon: Icons.Dashboard },
    { path: '/sales', label: '매출', icon: Icons.DollarSign },
    { path: '/zone-price', label: 'Price List', icon: Icons.Grid },
    { path: '/inventory', label: '재고 목록', icon: Icons.Inventory },
    { path: '/add', label: '재고 등록', icon: Icons.Add },
    { path: '/scan', label: '바코드 스캔', icon: Icons.Scan },
    { path: '/barcode-print', label: '바코드 출력', icon: Icons.Printer },
    {
      label: '선구매 리스트',
      icon: Icons.ShoppingBag,
      isGroup: true,
      children: [
        { path: '/pre-purchase', label: '선구매 리스트', icon: Icons.List },
        { path: '/container-pre-purchase', label: '컨테이너 선구매', icon: Icons.Truck },
      ]
    },
    {
      label: '데이터 관리',
      icon: Icons.List,
      isGroup: true,
      children: [
        { path: '/locations', label: '위치', icon: Icons.MapPin },
        { path: '/attributes', label: '속성', icon: Icons.Tag },
        { path: '/customers', label: '거래처', icon: Icons.Users },
        { path: '/filters', label: '필터 항목', icon: Icons.Filter },
        { path: '/excel', label: '엑셀 관리', icon: Icons.Excel },
      ]
    },
    {
      label: '설정',
      icon: Icons.Settings,
      isGroup: true,
      children: [
        { path: '/settings', label: '연동 설정', icon: Icons.Settings },
        { path: '/settings?tab=sales', label: '매출 설정', icon: Icons.DollarSign },
        { path: '/alerts', label: '알림 설정', icon: Icons.Bell },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const baseClasses = "flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-150 ease-in-out mb-1";
    const activeClasses = "bg-brand-800 text-white shadow-sm";
    const inactiveClasses = "text-brand-300 hover:bg-brand-800 hover:text-white";

    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`${baseClasses} ${active ? activeClasses : inactiveClasses}`}
      >
        <span className="mr-3"><Icon /></span>
        {item.label}
      </Link>
    );
  };

  const toggleMenu = (menuLabel) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuLabel]: !prev[menuLabel]
    }));
  };

  const MenuGroup = ({ item, mobile = false }) => {
    const Icon = item.icon;
    const isOpen = openMenus[item.label];

    return (
      <div className="mb-1">
        <button
          onClick={() => toggleMenu(item.label)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-150 ease-in-out text-brand-300 hover:bg-brand-800 hover:text-white"
        >
          <div className="flex items-center">
            <span className="mr-3"><Icon /></span>
            {item.label}
          </div>
          <Icons.ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const active = isActive(child.path);
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={() => mobile && setSidebarOpen(false)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ease-in-out ${active ? 'bg-brand-800 text-white shadow-sm' : 'text-brand-300 hover:bg-brand-800 hover:text-white'
                    }`}
                >
                  <span className="mr-3"><ChildIcon className="w-4 h-4" /></span>
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-brand-50 flex">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-brand-900 text-white z-50 h-16 flex items-center justify-between px-4 shadow-md">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-brand-800 active:bg-brand-700 transition-colors"
            aria-label="메뉴 열기/닫기"
          >
            {sidebarOpen ? (
              <Icons.Close className="w-7 h-7" />
            ) : (
              <Icons.Menu className="w-7 h-7" />
            )}
          </button>
          <span className="font-bold text-lg">원오디너리맨션</span>
        </div>
        {/* 알림 벨 (모바일) */}
        {user && <NotificationBell userId={user.email} />}
      </div>

      {/* Sidebar Backdrop (Mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:fixed inset-y-0 left-0 z-40 w-64 bg-brand-900 text-white transform transition-transform duration-200 ease-in-out shadow-xl
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${desktopSidebarOpen ? 'md:translate-x-0' : 'md:-translate-x-full'}
        flex flex-col
      `}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-brand-800">
          <h1 className="text-xl font-bold tracking-tight">원오디너리맨션</h1>
          {/* Desktop sidebar toggle button */}
          <button
            onClick={() => setDesktopSidebarOpen(false)}
            className="hidden md:flex p-2 rounded-lg hover:bg-brand-800 transition-colors"
            aria-label="사이드바 닫기"
          >
            <Icons.Menu className="w-5 h-5 text-white" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {menuItems.map((item, index) =>
              item.isGroup ? (
                <MenuGroup key={`group-${index}`} item={item} mobile={true} />
              ) : (
                <NavLink key={item.path} item={item} mobile={true} />
              )
            )}
          </div>
        </nav>

        <div className="p-4 border-t border-brand-800">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-brand-800 rounded-lg transition-colors"
          >
            <span className="mr-3"><Icons.Logout /></span>
            로그아웃
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 flex flex-col min-w-0 overflow-hidden pt-16 md:pt-0 transition-all duration-200 ${desktopSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        {/* Desktop Header */}
        <div className="hidden md:flex items-center justify-between px-8 py-4 bg-white border-b border-brand-100 shadow-sm">
          {!desktopSidebarOpen ? (
            <button
              onClick={() => setDesktopSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="메뉴 열기"
            >
              <Icons.Menu className="w-6 h-6 text-gray-600" />
            </button>
          ) : (
            <div></div>
          )}
          {user && <NotificationBell userId={user.email} />}
        </div>

        <div className="flex-1 overflow-y-auto p-1 md:p-4">
          {children}
        </div>
      </main>
    </div>
  );
}

export default Layout;
